import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Calendar, MapPin, Users, Ticket, AlertTriangle } from "lucide-react";

export default function CreateEvent() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    venue: "",
    start_at: "",
    end_at: "",
    capacity: 300,
    ticket_count: 300,
  });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        capacity: Number(form.capacity),
        ticket_count: Number(form.ticket_count),
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      const { data } = await api.post("/events", payload);
      navigate(`/admin/events/${data.id}`);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="create-event-page">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mono-label mb-6 hover:text-[#002FA7]"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="mono-label text-[#666] mb-2">// CREATE EVENT</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-8">
          New Event
        </h1>

        <form onSubmit={submit} className="brut-card p-6 sm:p-8 space-y-6" data-testid="event-form">
          <div>
            <label htmlFor="name" className="mono-label block mb-2">
              Event Name *
            </label>
            <input
              id="name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="brut-input"
              placeholder="Summer Music Festival 2026"
              data-testid="event-name-input"
            />
          </div>

          <div>
            <label htmlFor="description" className="mono-label block mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="brut-input resize-none"
              placeholder="A short description of the event…"
              data-testid="event-description-input"
            />
          </div>

          <div>
            <label htmlFor="venue" className="mono-label block mb-2">
              <MapPin className="inline w-3 h-3 mr-1" /> Venue *
            </label>
            <input
              id="venue"
              required
              value={form.venue}
              onChange={(e) => update("venue", e.target.value)}
              className="brut-input"
              placeholder="Madison Square Garden, NYC"
              data-testid="event-venue-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_at" className="mono-label block mb-2">
                <Calendar className="inline w-3 h-3 mr-1" /> Start *
              </label>
              <input
                id="start_at"
                type="datetime-local"
                required
                value={form.start_at}
                onChange={(e) => update("start_at", e.target.value)}
                className="brut-input"
                data-testid="event-start-input"
              />
            </div>
            <div>
              <label htmlFor="end_at" className="mono-label block mb-2">
                <Calendar className="inline w-3 h-3 mr-1" /> End *
              </label>
              <input
                id="end_at"
                type="datetime-local"
                required
                value={form.end_at}
                onChange={(e) => update("end_at", e.target.value)}
                className="brut-input"
                data-testid="event-end-input"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="capacity" className="mono-label block mb-2">
                <Users className="inline w-3 h-3 mr-1" /> Capacity *
              </label>
              <input
                id="capacity"
                type="number"
                min={1}
                max={10000}
                required
                value={form.capacity}
                onChange={(e) => update("capacity", e.target.value)}
                className="brut-input"
                data-testid="event-capacity-input"
              />
            </div>
            <div>
              <label htmlFor="ticket_count" className="mono-label block mb-2">
                <Ticket className="inline w-3 h-3 mr-1" /> Tickets to generate *
              </label>
              <input
                id="ticket_count"
                type="number"
                min={1}
                max={10000}
                required
                value={form.ticket_count}
                onChange={(e) => update("ticket_count", e.target.value)}
                className="brut-input"
                data-testid="event-ticket-count-input"
              />
              <p className="mt-1 text-xs text-[#666] font-mono">
                Each gets a unique QR code.
              </p>
            </div>
          </div>

          {err && (
            <div
              className="border-2 border-[#FF3B30] bg-white p-3 flex items-start gap-2"
              data-testid="event-form-error"
            >
              <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
              <span className="text-sm font-medium">{err}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="brut-btn-accent flex-1 flex items-center justify-center gap-2"
              data-testid="event-submit-btn"
            >
              {submitting ? (
                <>
                  <div className="brut-spinner !w-5 !h-5" />
                  Creating tickets…
                </>
              ) : (
                "Create Event & Generate Tickets"
              )}
            </button>
            <Link to="/admin" className="brut-btn-secondary text-center" data-testid="event-cancel-btn">
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
